import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Resources, useResource } from "Translations/Resources";
import { useStoriesTokenQuery } from "Api/Queries/Stories/useStoriesTokenQuery";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { useStoriesFeed } from "Hooks/Stories/useStoriesFeed";
import { canAuthorStories } from "Utils/Stories/storyAuthoring";
import { isAuthenticated } from "Utils/AuthUtils";
import {
  prefetchGroupEntry,
  scheduleIdlePrefetch,
} from "Utils/Stories/storyMediaPrefetch";
import { MooneStoryPlaceholder } from "Components/Stories/MooneStoryPlaceholder";
import { OwnStoryAvatar } from "Components/Stories/OwnStoryAvatar";
import { StoriesBar } from "Components/Stories/StoriesBar";
import { StoriesBarSkeleton } from "Components/Stories/StoriesBarSkeleton";
import { StoriesBarViewport } from "Components/Stories/StoriesBarViewport";
import { StoryViewer } from "Components/Stories/StoryViewer";

export type Story = {
  /** Stream activity id — also the handle for marking the story watched. */
  id: string;
  imageUrl?: string;
  videoUrl?: string;
  text?: string;
  buttonLabel?: string;
  buttonUrl?: string;
  sheetTitle?: string;
  sheetDescription?: string;
  sheetPrice?: string;
  sheetPriceNote?: string;
  sheetImageUrl?: string;
  /** Stream activity time (ISO) — stories play oldest → newest within a source. */
  time?: string;
  /**
   * Per-story watched state (Stream Feeds V3 track_watched — per user, cross-device).
   * Playback starts at the first unread story.
   */
  isRead: boolean;
};

/** Stories from Mo.one admins present as the brand (name, logo, ring); merchants as themselves. */
export type StoryAuthorKind = "moone" | "merchant";

export type StoryGroup = {
  actorId: string;
  actorName: string;
  actorImage?: string;
  authorKind: StoryAuthorKind;
  stories: Array<Story>;
  /** True only when EVERY story of this source is read (ring greys only after a full view). */
  isRead: boolean;
};

/** Circles whose entry stories get warmed on bar render, in sorted order (≈ tap likelihood). */
const MAX_PREFETCHED_GROUPS = 8;

/**
 * Bar/autoplay order: sources with unviewed stories first, Mo.one before merchants within each
 * partition, otherwise Stream's feed order (newest first — JS sort is stable).
 */
const sortStoryGroups = (groups: Array<StoryGroup>): Array<StoryGroup> =>
  [...groups].sort((a, b) => {
    if (a.isRead !== b.isRead) {
      return a.isRead ? 1 : -1;
    }
    if (a.authorKind !== b.authorKind) {
      return a.authorKind === "moone" ? -1 : 1;
    }
    return 0;
  });

export const StoriesSection = () => {
  const { t } = useResource();
  const user = useLoggedInUser();
  const isUserAuthenticated = isAuthenticated(user);
  const currentUserRole = useCurrentUserRole();

  // The token endpoint also upserts author profiles and ensures the caller's timeline follows all
  // current story authors — there is no client-side setup step. The active persona comes from the
  // signed-in user state (the same source chat uses) — no extra contexts request on the dashboard.
  const { data: storiesToken, isLoading: isTokenLoading } =
    useStoriesTokenQuery(isUserAuthenticated, user?.activeContextPublicID);

  const {
    storyGroups: liveGroups,
    isLoading: isFeedLoading,
    markStoriesWatched,
  } = useStoriesFeed(
    storiesToken,
    t(Resources.Common.AppName),
    t(Resources.Common.AnonymousUser),
  );

  // isLoading is false for skipped/disabled queries (TanStack v5), so unauthenticated and
  // non-migrated users never see the skeleton — they go straight to the placeholder circle.
  const isLoading = isTokenLoading || isFeedLoading;

  // Selection is tracked by actorId, NOT by array index: storyGroups can re-sort mid-session
  // (the SDK state is live over WebSocket — new stories can arrive while the viewer is open),
  // and a positional index would then silently point the open viewer at a different source.
  const [selectedActorId, setSelectedActorId] = useState<string | null>(null);
  // Backward navigation enters the previous source at its LAST story; forward entry (bar tap,
  // auto-advance) starts at the source's first unread story.
  const [shouldStartAtEnd, setShouldStartAtEnd] = useState(false);
  // Closing keeps the viewer mounted while it fades out (isViewerClosing); the session only
  // ends on onExited.
  const [isViewerClosing, setIsViewerClosing] = useState(false);

  // Mirrors the post-story route gate (and the backend author admission): Admin, plus Merchant
  // once merchant authoring is switched on; never AdminLite.
  const canPostStories = canAuthorStories(currentUserRole);

  // `storyGroups` is the viewer's traversal order: own group first (when live), then the sorted
  // rest — auto-advance from the own story continues into other sources like any other circle.
  const { ownGroup, otherGroups, storyGroups } = useMemo(() => {
    // Admin stories live inside the merged brand group (MOONE_STORY_GROUP_ACTOR_ID), so an Admin
    // persona never matches here — fine, back-office identities don't land on this dashboard.
    const own = storiesToken
      ? liveGroups.find((group) => group.actorId === storiesToken.userId)
      : undefined;
    const others = sortStoryGroups(
      own
        ? liveGroups.filter((group) => group.actorId !== own.actorId)
        : liveGroups,
    );
    return {
      ownGroup: own,
      otherGroups: others,
      storyGroups: own ? [own, ...others] : others,
    };
  }, [liveGroups, storiesToken]);

  // The own circle is Instagram-pinned first and never competes in the read/unread sort. Only
  // personas that can author stories get it — a Customer would otherwise stare at a permanently
  // dimmed, non-interactive placeholder they can never use (hunters are excluded by the same
  // rule). The live own group keeps the circle for anyone who still has stories up after losing
  // the posting role; without it the bar's empty state is the grayed Mo.one circle — the section
  // itself always renders (non-migrated users included), so the dashboard layout never shifts.
  const isOwnStoryVisible =
    storiesToken != null && (canPostStories || ownGroup != null);

  // Watched state is per story (activity): the viewer reports every story it actually displays,
  // and the ids are marked watched in ONE batched call at session end (marking mid-session would
  // re-sort the bar under the open viewer). Stories the user never reached stay unread — the
  // source's ring stays colorful until fully viewed.
  const viewedStoryIdsRef = useRef<Set<string>>(new Set());
  const handleStoryViewed = useCallback((story: Story) => {
    if (!story.isRead) {
      viewedStoryIdsRef.current.add(story.id);
    }
  }, []);

  // Starts the fade-out; the session finishes in handleViewerExited.
  const requestCloseViewer = useCallback(() => {
    setIsViewerClosing(true);
  }, []);

  // Runs after the fade-out: flush mark-watched — rings/order then update through the SDK's
  // WebSocket state once the player is visually gone (no re-sort under the closing animation).
  // markStoriesWatched is fire-and-forget (the hook swallows rejections).
  const handleViewerExited = useCallback(() => {
    setSelectedActorId(null);
    setIsViewerClosing(false);
    const viewedStoryIds = Array.from(viewedStoryIdsRef.current);
    viewedStoryIdsRef.current = new Set();
    markStoriesWatched(viewedStoryIds);
  }, [markStoriesWatched]);

  // The index is derived per render, so a mid-session re-sort keeps the viewer on the same
  // actor and next/previous still step through the CURRENT row order.
  const { selectedGroupIndex, selectedGroup } = useMemo(() => {
    const index =
      selectedActorId === null
        ? -1
        : storyGroups.findIndex((group) => group.actorId === selectedActorId);
    return {
      selectedGroupIndex: index,
      selectedGroup: index >= 0 ? storyGroups[index] : undefined,
    };
  }, [selectedActorId, storyGroups]);

  // The selected group can drop out of the live feed while the viewer is open (the last story's
  // 24 h TTL expiring, or a persona switch emptying the groups). The viewer then unmounts without
  // its exit transition, so run the exit flush here instead. On a persona switch the flush is a
  // silent no-op by design — the old feed is already gone, and viewed ids must never be marked
  // against the wrong persona's feed.
  useEffect(() => {
    if (selectedActorId !== null && !selectedGroup) {
      handleViewerExited();
    }
  }, [selectedActorId, selectedGroup, handleViewerExited]);

  // Warm the media a tap is most likely to hit — each circle's entry story (+1) — once the bar
  // has content, idle-scheduled so it never competes with the dashboard paint. Prefetching is
  // CDN-only (zero Stream API requests), deduped per URL and disabled under Save-Data, so the
  // re-runs on live feed updates are effectively free.
  useEffect(() => {
    if (storyGroups.length === 0) {
      return;
    }
    const groupsToWarm = storyGroups.slice(0, MAX_PREFETCHED_GROUPS);
    return scheduleIdlePrefetch(() => {
      for (const group of groupsToWarm) {
        prefetchGroupEntry(group);
      }
    });
  }, [storyGroups]);

  // Auto-advance continues into the next circle — warm its entry while the current one plays
  // (the within-group one-ahead lives in StoryViewer, which owns the story index).
  useEffect(() => {
    if (selectedGroupIndex >= 0) {
      prefetchGroupEntry(storyGroups[selectedGroupIndex + 1]);
    }
  }, [selectedGroupIndex, storyGroups]);

  if (isLoading) {
    return (
      <StoriesBarViewport>
        <StoriesBarSkeleton />
      </StoriesBarViewport>
    );
  }

  const handleSelectGroup = (actorId: string) => {
    const group = storyGroups.find((g) => g.actorId === actorId);
    if (!group) {
      return;
    }
    setShouldStartAtEnd(false);
    setIsViewerClosing(false);
    setSelectedActorId(group.actorId);
  };

  const handleViewerComplete = () => {
    if (selectedGroupIndex < 0) {
      return;
    }
    // Instagram-style continuation: play the next source in the row, close after the last one.
    const nextGroup = storyGroups[selectedGroupIndex + 1];
    if (nextGroup) {
      setShouldStartAtEnd(false);
      setSelectedActorId(nextGroup.actorId);
    } else {
      requestCloseViewer();
    }
  };

  const handleViewerPreviousSource = () => {
    // At the very first source, tapping back on the first story is a no-op (Instagram behavior).
    if (selectedGroupIndex > 0) {
      setShouldStartAtEnd(true);
      setSelectedActorId(storyGroups[selectedGroupIndex - 1].actorId);
    }
  };

  return (
    <>
      <StoriesBarViewport>
        <StoriesBar
          storyGroups={otherGroups}
          onSelect={handleSelectGroup}
          placeholderSlot={
            storyGroups.length === 0 ? <MooneStoryPlaceholder /> : undefined
          }
          ownStorySlot={
            isOwnStoryVisible ? (
              <OwnStoryAvatar
                ownGroup={ownGroup}
                canPost={canPostStories}
                onOpenGroup={handleSelectGroup}
              />
            ) : undefined
          }
        />
      </StoriesBarViewport>
      {selectedGroup && (
        <StoryViewer
          storyGroup={selectedGroup}
          isOpen
          isClosing={isViewerClosing}
          onClose={requestCloseViewer}
          onComplete={handleViewerComplete}
          onPreviousSource={handleViewerPreviousSource}
          onStoryViewed={handleStoryViewed}
          onExited={handleViewerExited}
          shouldStartAtEnd={shouldStartAtEnd}
        />
      )}
    </>
  );
};
